import format from 'string-format';

import { ModelException } from '$shared/exceptions/ModelException';
import { sentry } from '$shared/helpers/logger/sentry';
import { CertificateType, MapCertificateTypeWithLabel } from '$provider/models/CertificateType';
import RelatedEntityType from '$shared/constants/RelatedEntityType';
import { CredReqEventStatusLabel } from '$shared/constants/CredReqEventStatus';
import { CredReqTypeLabel } from '$shared/constants/CredReqType';
import { data } from '$provider/lib/datas';
import { BoardAliasName } from '$provider/constants/AuditLogCustomLabel';
import { VerificationData } from '$shared/lib/appData/VerificationData';
import { schemaData } from '$shared/lib/data/SchemaData';

const StringFormat = format.create({
  highlight: (s) => `<b>${s}</b>`
});

// Avoid anchor tag for these message paramters
const MessageParamtersToAvoidAnchor = ['dismissalNote', 'reason'];

export class AuditLogMessageParser {
  public readonly formatedMessage: string;

  private readonly messageParameters: ObjectType<string> | undefined;
  // Only for UI display the filter value
  private readonly anchorTagMessage: string;

  constructor(
    messageTemplate: string,
    messageParameters: Record<string, any>,
    context: Record<string, any>
  ) {
    this.messageParameters = this.getFormattedMessageParameters(messageParameters);
    this.anchorTagMessage = this.getAnchorTagText(messageTemplate, context);
    this.formatedMessage = this.getFormattedMessage(messageTemplate);
  }

  // Only for UI display the filter value
  public get hyperlinkText(): string {
    if (!this.anchorTagMessage || !this.messageParameters) return '';
    return StringFormat(this.anchorTagMessage, this.messageParameters);
  }

  private validateEntities(context: Record<string, any>): boolean {
    // Has any entity id available other than provider
    const hasEntityId = context?.relatedEntities?.some(
      (entity: any) => entity.type !== RelatedEntityType.Provider
    );
    if (!hasEntityId) return false;

    // Restrict anchor tag for cvo and note log, 2DO BE need to parse the message template
    const isSatisied = context?.relatedEntities?.some((entity: any) =>
      [
        RelatedEntityType.CredentialingRequest,
        RelatedEntityType.Note,
        RelatedEntityType.User,
        RelatedEntityType.ProfileMerge
      ].includes(entity.type)
    );
    if (isSatisied) return false;

    return true;
  }

  private getAnchorTagText(messageTemplate: string, context: Record<string, any>): string {
    if (!messageTemplate) return '';

    const isValid: boolean = this.validateEntities(context);
    if (!isValid) return '';

    // Get starting index
    const startIndex = messageTemplate.indexOf('{');
    if (startIndex === -1) return '';

    // Get ending index
    let endIndex = messageTemplate.lastIndexOf('}');
    if (endIndex === -1) {
      const exception = ModelException.from(
        'AuditLogMessageParser',
        'getAnchorTagText',
        { messageTemplate },
        new Error('Invalid message template format recieved'),
        this
      );
      sentry.log(exception);
      return '';
    }

    // If the message parameter is exist from the `MessageParamtersToAvoidAnchor` no need to add the anchor tag
    const parameter = MessageParamtersToAvoidAnchor.find((param: string) =>
      messageTemplate.includes(`{${param}}`)
    );
    if (parameter) {
      const slicedTemplate = messageTemplate.substring(
        0,
        messageTemplate.indexOf(`{${parameter}}`)
      );
      endIndex = slicedTemplate.indexOf('}');
    }
    const endIndexLength = endIndex + 1;
    // Add 1 if the endindexLenght position has a value in the messagetemplate
    const additionalCount = messageTemplate[endIndexLength]?.trim() ? 1 : 0;
    const slicedMessage: string = messageTemplate.slice(
      startIndex,
      endIndexLength + additionalCount
    );

    return slicedMessage;
  }

  private getFormattedMessageParameters(
    messageParameters: ObjectType<any>
  ): ObjectType<string> | undefined {
    if (!messageParameters) return;
    try {
      const params: ObjectType<string> = {};
      Object.entries(messageParameters).forEach(([key, value]) => {
        if (['firstName', 'lastName'].includes(key)) {
          params[key] = value;
          return;
        }
        let val;
        //Parse BoardCerts label
        Object.keys(schemaData.BoardSource).includes(value) &&
          (val = BoardAliasName[value] || data.getBoardSourceLabel(value));

        // Parse certificate types
        value in CertificateType && (val = MapCertificateTypeWithLabel[value]);

        // Parse medicaid enrollment dataset
        VerificationData.MedicaidDataset.includes(value) &&
          (val = VerificationData.data?.getDatasetSourceName(value));

        // Parse Dataset naming
        !val &&
          Object.values(schemaData.Dataset).includes(value) &&
          (val = VerificationData.data?.getDatasetSourceName(value));

        //Parse Credentialing request
        const credRequestLabel = CredReqTypeLabel[value] || CredReqEventStatusLabel[value];
        credRequestLabel && (val = credRequestLabel);

        params[key] = val || value;
      });
      return params;
    } catch (error) {
      const exception = ModelException.from(
        'AuditLogMessageParser',
        'formatMessageParameters',
        { messageParameters },
        error,
        this
      );
      sentry.log(exception);
    }
  }

  private concateAnchorTag(messageTemplate: string): string {
    if (!messageTemplate) return '';
    if (!this.anchorTagMessage) return messageTemplate;
    return messageTemplate.replace(this.anchorTagMessage, `<a>${this.anchorTagMessage}</a>`);
  }

  private getFormattedMessage(messageTemplate: string): string {
    if (!messageTemplate) return '';
    if (!this.messageParameters) return messageTemplate;

    let message = this.concateAnchorTag(messageTemplate);
    try {
      const params: ObjectType<string> = {};
      Object.entries(this.messageParameters).forEach(([key, value]) => {
        params[key] = `<b>${value}</b>`;
      });
      message = StringFormat(message, params);
    } catch (error) {
      const exception = ModelException.from(
        'AuditLogMessageParser',
        'formatMessage',
        { messageTemplate, messageParameters: this.messageParameters },
        error,
        this
      );
      sentry.log(exception);
    }
    return message;
  }
}
